import React, { useRef } from "react";
import { Trans } from "react-i18next";
import styled, { keyframes } from "styled-components/macro";
import { Button, Slide } from "@mui/material";
import { useSeeHint } from "components/hint/Hint";
import { SUBSCRIPTION } from "../../profile/ProfileRouter.config";
import { useNavigate } from "react-router";
import { useViewerQuery } from "queries/viewerQuery";
import FeaturesHintAnimation, { FeaturesHintAnimationRef, TextRun } from "./FeaturesHintAnimation";
import FeaturesHintImage from "./FeaturesHintImage";
import { useGetState, useSetState } from "components/ReduxProvider";
import DialogFullWindow from "components/other/DialogFullWindow";
import { MOTIVATIONHINT } from "sharedJs__generated/constants";
import { useSetLastImpulse } from "hooks/useSetLastImpulse";
import { abTest1Features, abTest2Features, defaultFeatures } from "./featureScreens";
import { useABTest } from "../../../queries/users/useABTest";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding-left: calc(min(78px, 13vw));
  padding-right: calc(min(78px, 13vw));
`;

const STitle = styled.div`
  text-align: center;
  font-family: "Noto Sans", "Segoe UI", sans-serif;
  font-size: calc(18px + 1vw);
  font-weight: 600;
  margin: 0 -15px 20px;
  line-height: 1.2;
  opacity: 0;
  animation: ${fadeIn} 0.3s ease-in-out 0s forwards;
`;

const AnimWrap = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-height: 50%;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-in-out 0.6s forwards;
`;

const ImageWrap = styled.div`
  flex: 1 1 auto;
  max-height: 65%;
  margin: 0 -20px -5vh;
  opacity: 0;
  animation: ${fadeIn} 0.4s ease-in-out 0.4s forwards;
`;

const SButton = styled(Button)`
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-in-out 1.2s forwards;
  width: 100%;
  margin-bottom: calc(min(44px, 13vh));
  margin-top: calc(min(44px, 13vh));
`;

export type AnimatedFeature = {
  type: "animated";
  title: string;
  rive: string;
  texts?: TextRun[];
};

export type StaticFeature = {
  type: "static";
  title: string;
  image: string;
};

export type Feature = AnimatedFeature | StaticFeature;

const isAnimated = (f: Feature): f is AnimatedFeature => f.type === "animated";

type Props = {};

const FeaturesHint: React.FC<Props> = () => {
  const navigate = useNavigate();
  const { viewer, isSubscriber } = useViewerQuery();
  const seeHint = useSeeHint();
  const setFeatureIndex = useSetState("onboardingFeaturesScreenIndex");
  const featureIndex = useGetState("onboardingFeaturesScreenIndex");
  const setLastImpulse = useSetLastImpulse();
  const animationRef = useRef<FeaturesHintAnimationRef>(null);
  const featureScreensAbTest = useABTest("abtest_featureScreens");
  const featureScreensWithChromeAbTest = useABTest("abtest_featureScreensWithChrome");
  let features = defaultFeatures;
  if (featureScreensAbTest && featureScreensWithChromeAbTest) {
    features = abTest2Features;
  } else if (featureScreensAbTest) {
    features = abTest1Features;
  }

  const handleNextClick = () => {
    const currentIndex = featureIndex ?? 0;
    if (currentIndex < features.length - 1) {
      setFeatureIndex(currentIndex + 1);
      return;
    }

    seeHint(MOTIVATIONHINT);
    if (!viewer?.inviter && !isSubscriber) {
      setLastImpulse("onboarding2");
      navigate(SUBSCRIPTION.url());
    }
  };

  const handleAnimWrapFadedIn = () => {
    const f = features[featureIndex ?? 0];
    if (isAnimated(f)) {
      animationRef.current?.startAnimation();
    }
  };

  const feature = features[featureIndex ?? 0];

  return (
    <DialogFullWindow open={true} background={true} TransitionComponent={Slide} maxWidth={600}>
      <ModalContent key={`content-${featureIndex}`}>
        <div />
        <STitle>
          <Trans>{feature.title}</Trans>
        </STitle>
        {isAnimated(feature) ? (
          <AnimWrap key={`anim-${featureIndex}`} onAnimationEnd={handleAnimWrapFadedIn}>
            <FeaturesHintAnimation key={`rive-${featureIndex}`} feature={feature} ref={animationRef} />
          </AnimWrap>
        ) : (
          <ImageWrap>
            <FeaturesHintImage feature={feature as StaticFeature} />
          </ImageWrap>
        )}
        <SButton
          key={`button-${featureIndex}`}
          onClick={handleNextClick}
          variant="contained"
          size="large"
          disableElevation
        >
          <Trans>Continue</Trans>
        </SButton>
      </ModalContent>
    </DialogFullWindow>
  );
};

export default FeaturesHint;
